import axios from 'axios';

import { ensureFreshAccessToken, refreshToken } from '@shared/store/api/baseApi';

import { apiService } from './apiService';
import type { FileResponseDto } from './apiService';

export interface UploadKnowledgeBaseFileArgs {
  file: File;
  campaignId: string;
  onProgress?: (progress: number | null) => void;
}

export const createKnowledgeBaseUploadFormData = (file: File, campaignId: string): FormData => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', file.name);
  formData.append('campaignId', campaignId);

  return formData;
};

const uploadKnowledgeBaseFileRequest = async (
  { file, campaignId, onProgress }: UploadKnowledgeBaseFileArgs,
  accessToken: string | null,
): Promise<FileResponseDto> => {
  const formData = createKnowledgeBaseUploadFormData(file, campaignId);

  const response = await axios.post<FileResponseDto>(
    `${import.meta.env.VITE_API_URL}/v2/files`,
    formData,
    {
      withCredentials: true,
      ...(accessToken ? { headers: { authorization: `Bearer ${accessToken}` } } : {}),
      onUploadProgress: (event) => {
        onProgress?.(event.total ? Math.round((event.loaded / event.total) * 100) : null);
      },
    },
  );

  return response.data;
};

export const uploadKnowledgeBaseFile = async (
  args: UploadKnowledgeBaseFileArgs,
): Promise<FileResponseDto> => {
  let accessToken = await ensureFreshAccessToken();

  try {
    return await uploadKnowledgeBaseFileRequest(args, accessToken);
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) throw error;

    accessToken = await refreshToken({ silent: true });
    if (!accessToken) throw error;

    return uploadKnowledgeBaseFileRequest(args, accessToken);
  }
};

export const knowledgeBaseApi = apiService
  .enhanceEndpoints({
    addTagTypes: ['CampaignFiles'],
    endpoints: {
      campaignFileListControllerList: {
        providesTags: (_result, _error, arg) => [{ type: 'CampaignFiles', id: arg.id }],
        extraOptions: { silent: true },
      },
      campaignFileSearchControllerSearch: {
        extraOptions: { silent: true },
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      reindexKnowledgeBaseFile: build.mutation<unknown, { fileId: string }>({
        query: ({ fileId }) => ({
          url: `/v2/files/${fileId}/reindex`,
          method: 'POST',
        }),
      }),
    }),
    overrideExisting: false,
  });
